import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { adminApplicationsTourId, adminApplicationsSteps } from '@/tours/admin';
import {
  FileText,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  XCircle,
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
} from 'lucide-react';
import {
  Card,
  EmptyState,
  Pagination,
  Select,
  LoadingSpinner,
  ErrorMessage,
  Table,
  Button,
  Modal,
} from '@/components/ui';
import { adminApi } from '@/services/api/adminApi';
import type { BranchApplication } from '@/services/api/adminApi';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const ITEMS_PER_PAGE = 20;

// ===== Application Status =====
const APP_STATUS_CONFIG: Record<string, { label: string; colors: string; icon: typeof Clock }> = {
  PENDING: { label: 'На рассмотрении', colors: 'bg-amber-100 text-amber-700', icon: Clock },
  APPROVED: { label: 'Одобрена', colors: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  REJECTED: { label: 'Отклонена', colors: 'bg-red-100 text-red-700', icon: XCircle },
};

const statusOptions = [
  { value: '', label: 'Все статусы' },
  { value: 'PENDING', label: 'На рассмотрении' },
  { value: 'APPROVED', label: 'Одобрены' },
  { value: 'REJECTED', label: 'Отклонены' },
];

// ===== Working Hours =====
const DAY_NAMES: Record<string, string> = {
  monday: 'Понедельник',
  tuesday: 'Вторник',
  wednesday: 'Среда',
  thursday: 'Четверг',
  friday: 'Пятница',
  saturday: 'Суббота',
  sunday: 'Воскресенье',
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function formatWorkingHours(hours: Record<string, any> | null): { day: string; time: string }[] {
  if (!hours) return [];
  return DAY_ORDER.map((key) => {
    const val = hours[key];
    if (!val || val === null) return { day: DAY_NAMES[key] ?? key, time: 'Выходной' };
    if (val.open && val.close) return { day: DAY_NAMES[key] ?? key, time: `${val.open} — ${val.close}` };
    return { day: DAY_NAMES[key] ?? key, time: 'Выходной' };
  });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ===== Status Badge =====
const ApplicationStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config = APP_STATUS_CONFIG[status] ?? { label: status, colors: 'bg-gray-100 text-gray-700', icon: Clock };
  const Icon = config.icon;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium', config.colors)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// ===== Detail Panel =====
const ApplicationDetail: React.FC<{
  app: BranchApplication;
  onApprove: () => void;
  onReject: () => void;
  isReviewing: boolean;
}> = ({ app, onApprove, onReject, isReviewing }) => {
  const hours = formatWorkingHours(app.working_hours);

  return (
    <div className="px-4 md:px-6 py-4 bg-gray-50/80 border-t border-gray-100 animate-fadeIn">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Applicant Info */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" />
            Заявитель
          </h4>
          <div className="space-y-2 text-sm">
            <p className="text-gray-900 font-medium">{app.applicant_name}</p>
            <p className="flex items-center gap-1.5 text-gray-600">
              <Mail className="w-3.5 h-3.5 text-gray-400" />
              {app.applicant_email}
            </p>
            {app.applicant_phone && (
              <p className="flex items-center gap-1.5 text-gray-600">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {app.applicant_phone}
              </p>
            )}
          </div>
        </div>

        {/* Branch Info */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" />
            Пункт выдачи
          </h4>
          <div className="space-y-2 text-sm">
            <p className="text-gray-900 font-medium">{app.branch_name}</p>
            <p className="flex items-center gap-1.5 text-gray-600">
              <MapPin className="w-3.5 h-3.5 text-gray-400" />
              {app.branch_address}
            </p>
            {app.branch_phone && (
              <p className="flex items-center gap-1.5 text-gray-600">
                <Phone className="w-3.5 h-3.5 text-gray-400" />
                {app.branch_phone}
              </p>
            )}
            {(app.latitude != null || app.longitude != null) && (
              <p className="text-xs text-gray-400">
                Координаты: {app.latitude ?? '—'}, {app.longitude ?? '—'}
              </p>
            )}
          </div>
        </div>

        {/* Working Hours */}
        {hours.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              Часы работы
            </h4>
            <div className="space-y-1 text-sm">
              {hours.map(({ day, time }) => (
                <div key={day} className="flex justify-between gap-2">
                  <span className="text-gray-500 text-xs">{day}</span>
                  <span className={cn('text-xs font-medium', time === 'Выходной' ? 'text-gray-400' : 'text-gray-700')}>
                    {time}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status Meta */}
      <div className="mt-4 pt-4 border-t border-gray-200/60 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            Подана: {formatDateTime(app.created_at)}
          </span>
          {app.reviewed_at && (
            <span>Рассмотрена: {formatDateTime(app.reviewed_at)}</span>
          )}
          {app.rejection_reason && (
            <span className="text-red-600">
              Причина отказа: {app.rejection_reason}
            </span>
          )}
        </div>

        {app.status === 'PENDING' && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="primary"
              size="sm"
              onClick={onApprove}
              loading={isReviewing}
              className="!bg-emerald-600 hover:!bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4" />
              Одобрить
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={onReject}
              loading={isReviewing}
            >
              <XCircle className="w-4 h-4" />
              Отклонить
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Main Page =====
const AdminApplicationsPage: React.FC = () => {
  useTourAutoStart({ tourId: adminApplicationsTourId, steps: adminApplicationsSteps });
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Reject modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<BranchApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const filtersKey = JSON.stringify({ status: statusFilter, page });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'applications', filtersKey],
    queryFn: () =>
      adminApi.getApplications({
        status: statusFilter || undefined,
        page,
        limit: ITEMS_PER_PAGE,
      }),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { status: string; rejection_reason?: string } }) =>
      adminApi.reviewApplication(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'applications'] });
    },
  });

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value);
      setPage(1);
      setExpandedId(null);
    },
    [],
  );

  const handleApprove = useCallback(
    async (app: BranchApplication) => {
      try {
        await reviewMutation.mutateAsync({ id: app.id, payload: { status: 'APPROVED' } });
        toast.success(`Заявка «${app.branch_name}» одобрена`);
        setExpandedId(null);
      } catch (err) {
        toast.error(extractErrorMessage(err, 'Не удалось одобрить заявку'));
      }
    },
    [reviewMutation],
  );

  const openRejectModal = useCallback((app: BranchApplication) => {
    setRejectTarget(app);
    setRejectionReason('');
    setRejectModalOpen(true);
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectTarget) return;
    try {
      await reviewMutation.mutateAsync({
        id: rejectTarget.id,
        payload: { status: 'REJECTED', rejection_reason: rejectionReason || undefined },
      });
      toast.success(`Заявка «${rejectTarget.branch_name}» отклонена`);
      setRejectModalOpen(false);
      setRejectTarget(null);
      setExpandedId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Не удалось отклонить заявку'));
    }
  }, [rejectTarget, rejectionReason, reviewMutation]);

  const toggleExpand = useCallback((id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  const applications = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Заявки на регистрацию ОЦ</h1>
        <p className="text-sm text-gray-500 mt-1">
          Рассмотрение заявок на открытие пунктов выдачи
          {totalCount > 0 && <span className="text-gray-400"> • {totalCount} всего</span>}
        </p>
      </div>

      <Card>
        <Card.Header>
          <div className="flex flex-col sm:flex-row gap-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusChange}
              className="sm:w-52"
            />
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div data-tour="admin:applications-list">
          {isLoading ? (
            <LoadingSpinner size="lg" className="py-16" />
          ) : error ? (
            <ErrorMessage message="Не удалось загрузить заявки" onRetry={() => refetch()} className="py-12" />
          ) : applications.length === 0 ? (
            <EmptyState
              icon={<FileText className="w-8 h-8" />}
              title="Заявок не найдено"
              description={statusFilter ? 'Попробуйте изменить фильтр статуса' : 'Новые заявки пока не поступали'}
            />
          ) : (
            <>
              {/* Mobile: card view */}
              <div className="md:hidden divide-y divide-gray-100">
                {applications.map((app) => (
                  <div key={app.id}>
                    <button
                      onClick={() => toggleExpand(app.id)}
                      className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{app.applicant_name}</p>
                          <ApplicationStatusBadge status={app.status} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="truncate">{app.branch_name}</span>
                          <span>{formatDate(app.created_at)}</span>
                        </div>
                      </div>
                      {expandedId === app.id ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                      )}
                    </button>
                    {expandedId === app.id && (
                      <ApplicationDetail
                        app={app}
                        onApprove={() => handleApprove(app)}
                        onReject={() => openRejectModal(app)}
                        isReviewing={reviewMutation.isPending}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop: table view */}
              <div className="hidden md:block">
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>ФИО заявителя</Table.Head>
                      <Table.Head>Email</Table.Head>
                      <Table.Head>Название ОЦ</Table.Head>
                      <Table.Head>Адрес</Table.Head>
                      <Table.Head>Дата подачи</Table.Head>
                      <Table.Head>Статус</Table.Head>
                      <Table.Head className="w-10">{' '}</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {applications.map((app) => (
                      <React.Fragment key={app.id}>
                        <Table.Row onClick={() => toggleExpand(app.id)}>
                          <Table.Cell className="font-medium text-gray-900">
                            {app.applicant_name}
                          </Table.Cell>
                          <Table.Cell>
                            <span className="text-gray-600">{app.applicant_email}</span>
                          </Table.Cell>
                          <Table.Cell className="font-medium">
                            {app.branch_name}
                          </Table.Cell>
                          <Table.Cell>
                            <span className="text-gray-500 max-w-[200px] truncate block">{app.branch_address}</span>
                          </Table.Cell>
                          <Table.Cell>{formatDate(app.created_at)}</Table.Cell>
                          <Table.Cell>
                            <ApplicationStatusBadge status={app.status} />
                          </Table.Cell>
                          <Table.Cell>
                            {expandedId === app.id ? (
                              <ChevronUp className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            )}
                          </Table.Cell>
                        </Table.Row>
                        {expandedId === app.id && (
                          <tr>
                            <td colSpan={7}>
                              <ApplicationDetail
                                app={app}
                                onApprove={() => handleApprove(app)}
                                onReject={() => openRejectModal(app)}
                                isReviewing={reviewMutation.isPending}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
          </div>
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </Card.Footer>
        )}
      </Card>

      {/* Reject Modal */}
      <Modal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Отклонить заявку"
        description={rejectTarget ? `Заявка «${rejectTarget.branch_name}» от ${rejectTarget.applicant_name}` : undefined}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-1.5">
              Причина отказа
            </label>
            <textarea
              id="rejection-reason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Укажите причину отклонения..."
              rows={3}
              className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-400 hover:border-gray-300 placeholder-gray-400 resize-none"
            />
          </div>
          <div className="flex items-center gap-3 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setRejectModalOpen(false)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleRejectConfirm}
              loading={reviewMutation.isPending}
            >
              <XCircle className="w-4 h-4" />
              Отклонить
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminApplicationsPage;
